"use client";

import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { RpcNetworkError } from "@/lib/db-rpc/rpc-client";

function isNetworkError(error: unknown): boolean {
  if (error instanceof RpcNetworkError) return true;
  if (error instanceof TypeError && error.message === "Failed to fetch")
    return true;
  if (
    error instanceof Error &&
    (error.message.includes("NetworkError") ||
      error.message.includes("network error") ||
      error.message === "Load failed")
  )
    return true;
  return false;
}

function subscribeToOnlineStatus(callback: () => void) {
  window.addEventListener("online", callback);
  window.addEventListener("offline", callback);
  return () => {
    window.removeEventListener("online", callback);
    window.removeEventListener("offline", callback);
  };
}

/**
 * Subscribes to React Query cache and shows a toast when queries fail
 * with network errors. Prevents users from seeing empty/wrong data
 * without knowing a fetch failed. Throttled to one toast per 10 seconds.
 *
 * SENTRY FIX: NODE-268 — extended to catch TypeError: Failed to fetch
 * alongside RpcNetworkError so users always see connection feedback.
 *
 * SENTRY FIX: NODE-268/NODE-1TT/NODE-1ZD — Offline detection banner
 * Date: 2026-04-14 | Agent: Claude Code
 * Root cause: Users on flaky mobile connections saw transient toasts that
 *   disappeared, with no persistent indication of offline state.
 * Fix: Added navigator.onLine listener that shows a persistent banner
 *   when the device is offline, and auto-hides when back online.
 *
 * Uses useSyncExternalStore to read navigator.onLine without causing
 * hydration mismatches between server and client rendering.
 *
 * Must be placed inside both QueryClientProvider and ToastProvider.
 */
export function NetworkErrorNotifier() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const lastToastRef = useRef(0);

  const isOffline = useSyncExternalStore(
    subscribeToOnlineStatus,
    () => !navigator.onLine,
    () => false
  );

  const invalidateOnReconnect = useCallback(() => {
    if (navigator.onLine) {
      queryClient.invalidateQueries();
    }
  }, [queryClient]);

  useEffect(() => {
    window.addEventListener("online", invalidateOnReconnect);
    return () => window.removeEventListener("online", invalidateOnReconnect);
  }, [invalidateOnReconnect]);

  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const unsubscribe = cache.subscribe((event) => {
      if (
        event.type === "updated" &&
        event.query.state.status === "error" &&
        isNetworkError(event.query.state.error)
      ) {
        const now = Date.now();
        if (now - lastToastRef.current > 10_000) {
          lastToastRef.current = now;
          const isDeviceOffline =
            typeof navigator !== "undefined" && !navigator.onLine;
          toast({
            title: isDeviceOffline
              ? "You appear to be offline"
              : "Connection issue",
            description: isDeviceOffline
              ? "Check your internet connection. We'll retry when you're back online."
              : "Some data couldn't be loaded. We'll retry automatically.",
            variant: "destructive",
          });
        }
      }
    });
    return unsubscribe;
  }, [queryClient, toast]);

  if (!isOffline) return null;

  return (
    <div className="bg-destructive/90 fixed top-0 right-0 left-0 z-[9999] px-4 py-2 text-center text-sm font-medium text-white backdrop-blur-sm">
      You appear to be offline. Some features may not work until your connection
      is restored.
    </div>
  );
}
